/**
 * 透视裁剪工具（M4-02 / DESIGN.md §6.5）。
 *
 * 四次点击放四个角点（左上→右上→右下→左下，顺时针）；放完后可以继续
 * 拖拽任意一个角点微调；Enter 提交（一条历史），Esc 取消（无历史）。
 */
import { perspectiveCropDocument } from '../../app/perspective-crop';
import type { Point } from '../../engine/transform/perspective';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

export interface PerspectiveCropDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

const HANDLE_HIT_RADIUS = 10;

export class PerspectiveCropToolController {
  readonly #deps: PerspectiveCropDeps;
  #corners: Point[] = [];
  #dragIndex = -1;

  constructor(deps: PerspectiveCropDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#corners.length > 0;
  }

  get corners(): readonly Point[] {
    return this.#corners;
  }

  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: Point): boolean {
    if (this.#deps.tools.active.id !== 'perspectiveCrop') return false;
    if (this.#deps.store.document === null) return true;

    if (phase === 'down') {
      if (this.#corners.length === 4) {
        const hit = this.#corners.findIndex((c) => Math.hypot(c.x - point.x, c.y - point.y) <= HANDLE_HIT_RADIUS);
        this.#dragIndex = hit;
        return true;
      }
      this.#corners.push(point);
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move' && this.#dragIndex >= 0) {
      this.#corners[this.#dragIndex] = point;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'up') {
      this.#dragIndex = -1;
      return true;
    }
    return true;
  }

  /** Enter：提交透视裁剪。返回是否发生了裁剪。 */
  commit(): boolean {
    if (this.#corners.length !== 4) return false;
    const corners = this.#corners as [Point, Point, Point, Point];
    this.#corners = [];
    this.#deps.requestRender();
    try {
      return perspectiveCropDocument(this.#deps.store, corners);
    } catch {
      // 四点退化（共线等）：拒绝提交，不留任何改动，会话已经清空
      return false;
    }
  }

  cancel(): void {
    this.#corners = [];
    this.#dragIndex = -1;
    this.#deps.requestRender();
  }

  /** 文档空间叠加预览：四边形轮廓 + 角点手柄。 */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    if (this.#corners.length === 0) return;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.#corners[0]!.x, this.#corners[0]!.y);
    for (let i = 1; i < this.#corners.length; i++) ctx.lineTo(this.#corners[i]!.x, this.#corners[i]!.y);
    if (this.#corners.length === 4) ctx.closePath();
    ctx.stroke();
    for (const corner of this.#corners) {
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
